import React, { useState, useEffect, useRef } from 'react';
import { useMutation } from '@tanstack/react-query';
import { PricingRequest } from '../types/api';
import { getPricing } from '../services/api';
import usePlacesAutocomplete, { getGeocode } from 'use-places-autocomplete';
import { PORTS, Port } from '../constants/ports';

const CONTAINER_SIZES = ['20', '40', '45'];

interface PricingFormProps {
  onPricingComplete: (quoteId: number, port: string, details: any) => void;
  onPortChange: (port: string) => void;
}

export const PricingForm: React.FC<PricingFormProps> = ({ onPricingComplete, onPortChange }) => {
  const [isPortDropdownOpen, setIsPortDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    ready,
    value,
    suggestions: { status: placeStatus, data: placeData },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      componentRestrictions: { country: 'us' },
      types: ['(regions)']
    },
    debounce: 300,
  });

  const [formData, setFormData] = useState<PricingRequest>({
    terminalKey: '',
    city: '',
    state: '',
    zipCode: '',
    isReefer: false,
    isBulkLiquid: false,
    ishazFee: false,
    overWeight: false,
    bulkLiquid: '',
    cargoWeight: '',
    containerSize: '40',
    customerId: '',
    customerEmail: '',
    chassisType: '',
    importExport: 'Import'
  });

  const [toggles, setToggles] = useState({
    hazardous: false,
    reefer: false,
    overWeight: false,
    bulkliquid: false
  });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsPortDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (formData.cargoWeight && formData.containerSize) {
      const weight = parseInt(formData.cargoWeight);
      const isOverweight = 
        (formData.containerSize === '20' && weight > 42000) ||
        (formData.containerSize === '40' && weight > 44500);
      
      setToggles(prev => ({
        ...prev,
        overWeight: isOverweight
      }));
      
      setFormData(prev => ({
        ...prev,
        overWeight: isOverweight
      }));
    }
  }, [formData.cargoWeight, formData.containerSize]);

  const pricingMutation = useMutation({
    mutationFn: getPricing,
    onSuccess: (data) => {
      if (data.status === 'Success' && data.data.quoteId) {
        const details = {
          city: data.data.city.name,
          state: data.data.city.state,
          zipCode: data.data.zipCode,
          cityFullName: data.data.cityFullName,
          terminal: formData.terminalKey,
          terminalLocation: data.data.terminal.terminalKey,
          loadType: formData.importExport,
          miles: data.data.onewayMiles,
          statesCrossed: data.data.statesCrossed,
          lineHaul: data.data.charges.ItemPrice,
          fscSurcharge: data.data.charges.fscSurcharge,
          travelTime: data.data.travelTime.fullTime,
          chassisDays: data.data.charges.chassisDays,
          chassis: data.data.chassis,
          total: data.data.quoteTotalPrice,
          mileageSurcharge: data.data.charges.mileageSurcharge,
          tolls: data.data.charges.tolls,
          reeferFee: data.data.charges.reeferFee,
          bulkLiquidFee: data.data.charges.bulkLiquidFee,
          hazFee: data.data.charges.hazFee,
          overweightTotal: data.data.charges.overweightTotal,
          overweightDetails: data.data.overweight,
          toggles: {
            hazardous: toggles.hazardous ? data.data.charges.hazFee : 0,
            reefer: toggles.reefer ? data.data.charges.reeferFee : 0,
            overweight: data.data.overweight?.charges.reduce((acc: number, charge: any) => acc + charge.rate, 0) || 0
          }
        };
        onPricingComplete(data.data.quoteId, formData.terminalKey, details);
      }
    },
  });

  const handlePortSelect = (port: Port) => {
    setFormData(prev => ({
      ...prev,
      terminalKey: port.key
    }));
    onPortChange(port.key);
    setIsPortDropdownOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (name === 'terminalKey') {
      onPortChange(value);
    }
  };

  const handleLocationSelect = async (suggestion: { description: string; place_id: string }) => {
    const description = suggestion.description.replace(', USA', '');
    setValue(description, false);
    clearSuggestions();

    try {
      const results = await getGeocode({ address: suggestion.description });
      const addressComponents = results[0].address_components;
      
      let city = '', state = '', zipCode = '';
      
      addressComponents.forEach((component: any) => {
        if (component.types.includes('locality')) {
          city = component.long_name;
        } else if (component.types.includes('administrative_area_level_1')) {
          state = component.short_name;
        } else if (component.types.includes('postal_code')) {
          zipCode = component.long_name;
        }
      });

      setFormData(prev => ({
        ...prev,
        city,
        state,
        zipCode
      }));

      setValue(`${city}, ${state}, ${zipCode}`, false);
    } catch (error) {
      console.error('Error: ', error);
    }
  };

  const handleToggleChange = (name: keyof typeof toggles) => {
    const newValue = !toggles[name];
    setToggles(prev => ({
      ...prev,
      [name]: newValue
    }));

    setFormData(prev => ({
      ...prev,
      isReefer: name === 'reefer' ? newValue : prev.isReefer,
      isBulkLiquid: name === 'bulkliquid' ? newValue : prev.isBulkLiquid,
      ishazFee: name === 'hazardous' ? newValue : prev.ishazFee,
      overWeight: name === 'overWeight' ? newValue : prev.overWeight
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.terminalKey) {
      alert('Please select a port');
      return;
    }
    if (!formData.city || !formData.state) {
      alert('Please select a destination');
      return;
    }
    const { city, state, zipCode, ...rest } = formData;
    const requestData = {
      ...rest,
      city,
      state,
      zipCode
    };
    pricingMutation.mutate(requestData);
  };

  return (
    <div className="card max-w-4xl mx-auto bg-white shadow-lg rounded-lg p-6">
      <h2 className="text-2xl font-bold text-[#3B5B3C] mb-6">Rate Selection</h2>
      
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Import/Export Selection */}
        <div className="grid grid-cols-2 gap-4">
          <button
            type="button"
            onClick={() => setFormData(prev => ({ ...prev, importExport: 'Import' }))}
            className={`import-export-button ${formData.importExport === 'Import' ? 'active' : ''}`}
          >
            Import
          </button>
          <button
            type="button"
            onClick={() => setFormData(prev => ({ ...prev, importExport: 'Export' }))}
            className={`import-export-button ${formData.importExport === 'Export' ? 'active' : ''}`}
          >
            Export
          </button>
        </div>

        {/* Custom Port Selection Dropdown */}
        <div className="relative" ref={dropdownRef}>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Port Selection
          </label>
          <div
            className="w-full h-12 pl-4 border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary text-base flex items-center justify-between cursor-pointer"
            onClick={() => setIsPortDropdownOpen(!isPortDropdownOpen)}
          >
            <span className={formData.terminalKey ? 'text-gray-900' : 'text-gray-500'}>
              {formData.terminalKey ? PORTS.find(p => p.key === formData.terminalKey)?.name : 'Select Port'}
            </span>
            <svg
              className={`h-5 w-5 text-gray-400 mr-2 transition-transform ${isPortDropdownOpen ? 'transform rotate-180' : ''}`}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          
          {isPortDropdownOpen && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
              {PORTS.map((port) => (
                <div
                  key={port.key}
                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer text-gray-900"
                  onClick={() => handlePortSelect(port)}
                >
                  {port.name}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Location Input */}
        <div className="relative">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            City, State, or Zip
          </label>
          <div className="relative">
            <input
              value={value}
              onChange={e => setValue(e.target.value)}
              disabled={!ready}
              className="w-full h-12 pl-4 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary text-base"
              placeholder="Start typing a city, state, or ZIP"
            />
            {placeStatus === 'OK' && (
              <ul className="absolute z-10 w-full bg-white border border-gray-300 rounded-md mt-1 shadow-lg max-h-60 overflow-auto">
                {placeData.map(suggestion => (
                  <li
                    key={suggestion.place_id}
                    onClick={() => handleLocationSelect(suggestion)}
                    className="px-4 py-3 cursor-pointer hover:bg-gray-50 text-base"
                  >
                    {suggestion.description.replace(', USA', '')}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Container Size Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Container Size
          </label>
          <div className="container-size-grid">
            {CONTAINER_SIZES.map(size => (
              <button
                key={size}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, containerSize: size }))}
                className={`container-size-button ${formData.containerSize === size ? 'active' : ''}`}
              >
                {size} ft
              </button>
            ))}
          </div>
        </div>

        {/* Toggles */}
        <div className="flex flex-wrap gap-4 mt-4 justify-between">
          {Object.entries(toggles).map(([key, value]) => (
            <label key={key} className="inline-flex items-center cursor-pointer">
              <div className="relative">
                <input
                  type="checkbox"
                  className="sr-only"
                  checked={value}
                  onChange={() => handleToggleChange(key as keyof typeof toggles)}
                />
                <div className={`toggle-switch ${value ? 'active bg-primary' : 'bg-gray-200'}`} />
              </div>
              <span className="ml-2 text-sm font-medium text-gray-700 capitalize">
                {key}
              </span>
            </label>
          ))}
        </div>

        {/* Weight Input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Cargo Weight (lbs)
          </label>
          <input
            type="number"
            name="cargoWeight"
            value={formData.cargoWeight}
            onChange={handleInputChange}
            className="w-full h-12 px-4 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary text-base"
            placeholder="Enter weight in lbs"
          />
        </div>

        <div className="flex justify-end space-x-4">
          <button
            type="submit"
            className="bg-primary text-white px-8 h-12 rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 text-base font-medium"
            disabled={pricingMutation.isPending}
          >
            {pricingMutation.isPending ? 'Calculating...' : 'Get Rate'}
          </button>
        </div>

        {pricingMutation.isError && (
          <div className="mt-4 p-4 bg-red-50 text-red-700 rounded-md">
            An error occurred while calculating the rate. Please try again.
          </div>
        )}
      </form>
    </div>
  );
};
